import React, { useEffect, useState, useRef, Fragment } from 'react';
import { Cpu, Search, Download, Eye, RefreshCw, AlertTriangle, CheckCircle2, Clock, XCircle, Zap, BarChart3, FileText, Settings, Activity, TrendingUp, MoreVertical, Play, Pause, DollarSign, Server, X, Ban, FileCode2, MessageSquare, ThumbsDown, Trash2, Send, Code, Terminal, Database, Save, Star, Shield, Wifi, WifiOff, Gauge, ArrowUp, ArrowDown, CircleDot, Hash, Percent, Timer, AlertCircle, ChevronRight, Radio, Layers, Target, TrendingDown, PieChart as PieChartIcon } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { Pagination } from '../ui/Pagination';
import { Input } from '../ui/Input';
import { AISandboxModal } from './AISandboxModal';
import { AIQueueManager } from './AIQueueManager';
import { AIAllGenerationsManager } from './AIAllGenerationsManager';
import { AIErrorControlCenter } from './AIErrorControlCenter';
import { AITemplateEngine } from './AITemplateEngine';
import { AIQualityControlCenter } from './AIQualityControlCenter';
import { AIEngineControlCenter } from './AIEngineControlCenter';
// --- Fixed-position Tooltip ---
function Tooltip({
  children,
  text
}: {
  children: React.ReactNode;
  text: string;
}) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({
    top: 0,
    left: 0
  });
  const ref = useRef<HTMLSpanElement>(null);
  const handleEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({
        top: rect.top - 4,
        left: rect.left + rect.width / 2
      });
    }
    setShow(true);
  };
  return <span ref={ref} className="relative inline-flex" onMouseEnter={handleEnter} onMouseLeave={() => setShow(false)} data-id="element-1439">
      {children}
      {show && <span className="fixed px-2.5 py-1.5 text-[10px] font-medium text-white bg-slate-800 rounded-lg whitespace-nowrap z-[9999] shadow-xl pointer-events-none -translate-x-1/2 -translate-y-full" style={{
      top: pos.top,
      left: pos.left
    }} data-id="element-1440">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800" data-id="element-1441" />
        </span>}
    </span>;
}
interface AdminAIReportsManagerProps {
  users: any[];
  astrologers: any[];
  setActiveTab: (tab: string) => void;
  setCreatePanel: (panel: any) => void;
  setDetailPanel: (panel: any) => void;
  success: (msg: string) => void;
}
const statusConfig: Record<string, {
  label: string;
  variant: 'success' | 'warning' | 'error' | 'neutral' | 'pro';
}> = {
  ready: {
    label: 'Готово',
    variant: 'success'
  },
  generating: {
    label: 'Генерация',
    variant: 'warning'
  },
  queued: {
    label: 'В очереди',
    variant: 'neutral'
  },
  error: {
    label: 'Ошибка',
    variant: 'error'
  },
  review: {
    label: 'На проверке',
    variant: 'pro'
  },
  cancelled: {
    label: 'Отменено',
    variant: 'error'
  }
};
export function AdminAIReportsManager({
  users,
  astrologers,
  setActiveTab,
  setCreatePanel,
  setDetailPanel,
  success
}: AdminAIReportsManagerProps) {
  const [activeTabId, setActiveTabId] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    model: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [showSandbox, setShowSandbox] = useState(false);
  // --- NEW STATE FOR OVERVIEW ---
  const [liveStats, setLiveStats] = useState({
    activeGens: 3,
    queueLoad: 12,
    avgLatency: 48,
    failuresLast5m: 0,
    totalToday: 87
  });
  const [engineStatus, setEngineStatus] = useState<'active' | 'degraded' | 'error' | 'maintenance'>('active');
  const [enginePaused, setEnginePaused] = useState(false);
  const [overviewDrill, setOverviewDrill] = useState<{
    title: string;
    type: string;
  } | null>(null);
  useEffect(() => {
    const interval = setInterval(() => {
      if (!enginePaused) {
        setLiveStats(prev => ({
          activeGens: Math.max(0, prev.activeGens + (Math.random() > 0.5 ? 1 : -1)),
          queueLoad: Math.max(0, prev.queueLoad + (Math.random() > 0.7 ? 1 : -1)),
          avgLatency: Math.max(20, Math.min(120, prev.avgLatency + (Math.random() * 10 - 5))),
          failuresLast5m: Math.random() > 0.9 ? prev.failuresLast5m + 1 : prev.failuresLast5m,
          totalToday: prev.totalToday + (Math.random() > 0.8 ? 1 : 0)
        }));
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [enginePaused]);
  // --- MOCK DATA ---
  const mockGenerations = [{
    id: 'GEN-4201',
    user: 'Анна Петрова',
    order: 'ORD-1201',
    type: 'Натальная карта',
    model: 'GPT-5',
    cost: 0.15,
    duration: '42 сек',
    status: 'ready',
    date: '15.03.2026 14:20',
    retries: 0,
    quality: 4.8,
    tokens: 4500
  }, {
    id: 'GEN-4202',
    user: 'Иван Сидоров',
    order: 'ORD-1202',
    type: 'Совместимость',
    model: 'Claude 3.5',
    cost: 0.12,
    duration: '58 сек',
    status: 'ready',
    date: '15.03.2026 14:15',
    retries: 0,
    quality: 4.5,
    tokens: 3800
  }, {
    id: 'GEN-4203',
    user: 'Maria Schmidt',
    order: 'ORD-1203',
    type: 'Прогноз',
    model: 'GPT-5',
    cost: 0.0,
    duration: '—',
    waitTime: '2 мин',
    status: 'generating',
    date: '15.03.2026 14:10',
    retries: 0,
    quality: null,
    tokens: 0
  }, {
    id: 'GEN-4204',
    user: 'Олег Козлов',
    order: 'ORD-1204',
    type: 'Соляр',
    model: 'Llama 3',
    cost: 0.08,
    duration: '—',
    status: 'error',
    error: 'Timeout: generation exceeded 120s limit',
    date: '15.03.2026 13:55',
    retries: 2,
    quality: null,
    tokens: 1200
  }, {
    id: 'GEN-4205',
    user: 'Елена Волкова',
    order: 'ORD-1205',
    type: 'Комбо',
    model: 'GPT-5',
    cost: 0.25,
    duration: '1м 12с',
    status: 'review',
    date: '15.03.2026 13:40',
    retries: 0,
    quality: 2.1,
    complaint: 'Много повторов текста',
    tags: ['повтор текста'],
    tokens: 8500
  }, {
    id: 'GEN-4206',
    user: 'Pierre Dupont',
    order: 'ORD-1206',
    type: 'Натальная карта',
    model: 'GPT-5',
    cost: 0.15,
    duration: '45 сек',
    status: 'ready',
    date: '15.03.2026 13:30',
    retries: 0,
    quality: 4.9,
    tokens: 4600
  }, {
    id: 'GEN-4207',
    user: 'Дмитрий Новиков',
    order: 'ORD-1207',
    type: 'Прогноз',
    model: 'Claude 3.5',
    cost: 0.0,
    duration: '—',
    waitTime: '5 мин',
    status: 'queued',
    date: '15.03.2026 13:20',
    retries: 0,
    quality: null,
    tokens: 0
  }, {
    id: 'GEN-4208',
    user: 'Светлана Иванова',
    order: 'ORD-1208',
    type: 'Совместимость',
    model: 'GPT-5',
    cost: 0.0,
    duration: '—',
    waitTime: '3 мин',
    status: 'queued',
    date: '15.03.2026 13:10',
    retries: 1,
    quality: null,
    tokens: 0
  }, {
    id: 'GEN-4209',
    user: 'Алексей Смирнов',
    order: 'ORD-1209',
    type: 'Натальная карта',
    model: 'Llama 3',
    cost: 0.05,
    duration: '20 сек',
    status: 'cancelled',
    date: '15.03.2026 12:50',
    retries: 0,
    quality: null,
    tokens: 500
  }, {
    id: 'GEN-4210',
    user: 'Ольга Кузнецова',
    order: 'ORD-1210',
    type: 'Прогноз',
    model: 'GPT-5',
    cost: 0.18,
    duration: '55 сек',
    status: 'ready',
    date: '15.03.2026 12:30',
    retries: 0,
    quality: 2.5,
    complaint: 'Слишком короткий ответ',
    tags: ['short answer'],
    tokens: 2100
  }];
  const mockErrors = [{
    id: 'ERR-301',
    genId: 'GEN-4204',
    user: 'Олег Козлов',
    type: 'Соляр',
    model: 'Llama 3',
    error: 'API Timeout',
    date: '15.03.2026 13:57',
    retries: 2
  }, {
    id: 'ERR-300',
    genId: 'GEN-4198',
    user: 'Мария Кузнецова',
    type: 'Комбо',
    model: 'GPT-5',
    error: 'Rate limit exceeded',
    date: '15.03.2026 12:30',
    retries: 1
  }, {
    id: 'ERR-299',
    genId: 'GEN-4190',
    user: 'John Smith',
    type: 'Натальная карта',
    model: 'Claude 3.5',
    error: 'Token overflow (max 8192)',
    date: '15.03.2026 10:15',
    retries: 3
  }];
  const mockTemplates = [{
    id: 'TPL-01',
    name: 'Натальная карта',
    status: 'active',
    version: 'v3.2',
    updated: '10.03.2026',
    prompt: 'You are an expert astrologer. Analyze the natal chart based on the provided planetary positions...',
    structure: ['Intro', 'Анализ личности', 'Кармические задачи', 'Советы'],
    params: ['birthDate', 'birthTime', 'birthPlace']
  }, {
    id: 'TPL-02',
    name: 'Соляр',
    status: 'active',
    version: 'v2.8',
    updated: '08.03.2026',
    prompt: 'Generate a solar return report focusing on the main themes of the upcoming year...',
    structure: ['Главная тема года', 'Финансы', 'Отношения', 'Здоровье'],
    params: ['birthDate', 'currentYear', 'location']
  }, {
    id: 'TPL-03',
    name: 'Совместимость',
    status: 'active',
    version: 'v3.0',
    updated: '12.03.2026',
    prompt: 'Analyze the synastry chart between Person A and Person B...',
    structure: ['Общая совместимость', 'Эмоциональная связь', 'Конфликты', 'Перспективы'],
    params: ['personA_data', 'personB_data']
  }, {
    id: 'TPL-04',
    name: 'Прогноз',
    status: 'active',
    version: 'v3.2',
    updated: '10.03.2026',
    prompt: 'Provide a transit forecast for the next 30 days...',
    structure: ['Общий фон', 'Ключевые даты', 'Предостережения'],
    params: ['birthData', 'period']
  }, {
    id: 'TPL-05',
    name: 'Комбо',
    status: 'draft',
    version: 'v1.0',
    updated: '14.03.2026',
    prompt: 'Combine natal, solar, and transit analysis into one comprehensive report...',
    structure: ['Натал', 'Соляр', 'Транзиты', 'Синтез'],
    params: ['all_data']
  }];
  useEffect(() => {
    const handleClickOutside = () => setOpenActionMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  // --- RENDER TABS ---
  const renderOverview = () => {
    // Chart Data
    const chartData = [{
      date: '01.03',
      success: 65,
      failed: 2,
      cancelled: 5
    }, {
      date: '02.03',
      success: 78,
      failed: 4,
      cancelled: 3
    }, {
      date: '03.03',
      success: 92,
      failed: 1,
      cancelled: 8
    }, {
      date: '04.03',
      success: 85,
      failed: 5,
      cancelled: 4
    }, {
      date: '05.03',
      success: 110,
      failed: 2,
      cancelled: 6
    }, {
      date: '06.03',
      success: 95,
      failed: 8,
      cancelled: 2
    }, {
      date: '07.03',
      success: 87,
      failed: 3,
      cancelled: 5
    }, {
      date: '08.03',
      success: 105,
      failed: 1,
      cancelled: 4
    }, {
      date: '09.03',
      success: 120,
      failed: 2,
      cancelled: 7
    }, {
      date: '10.03',
      success: 115,
      failed: 4,
      cancelled: 3
    }, {
      date: '11.03',
      success: 130,
      failed: 1,
      cancelled: 5
    }, {
      date: '12.03',
      success: 142,
      failed: 6,
      cancelled: 8
    }, {
      date: '13.03',
      success: 128,
      failed: 3,
      cancelled: 4
    }, {
      date: '14.03',
      success: 156,
      failed: 2,
      cancelled: 6
    }];
    return <div className="space-y-6" data-id="element-1442">
        {/* 1. LIVE STATUS BAR */}
        <div className="bg-slate-900 rounded-xl p-3 flex flex-col lg:flex-row items-center justify-between gap-4 shadow-lg border border-slate-800" data-id="element-1443">
          <div className="flex items-center gap-6 overflow-x-auto w-full lg:w-auto pb-2 lg:pb-0 scrollbar-hide" data-id="element-1444">
            <div className="flex items-center gap-2 shrink-0" data-id="element-1445">
              <div className="relative flex h-3 w-3" data-id="element-1446">
                {engineStatus === 'active' && !enginePaused && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" data-id="element-1447"></span>}
                <span className={`relative inline-flex rounded-full h-3 w-3 ${enginePaused ? 'bg-amber-500' : engineStatus === 'active' ? 'bg-emerald-500' : 'bg-rose-500'}`} data-id="element-1448"></span>
              </div>
              <span className="text-xs font-bold text-white uppercase tracking-wider" data-id="element-1449">
                {enginePaused ? 'Engine Paused' : 'Engine Active'}
              </span>
            </div>
            <div className="h-4 w-px bg-slate-700 shrink-0" data-id="element-1450" />
            <div className="flex items-center gap-2 shrink-0 text-slate-300 text-xs" data-id="element-1451">
              <Activity className="w-3.5 h-3.5 text-indigo-400" data-id="element-1452" />
              <span data-id="element-1453">
                Active:{' '}
                <strong className="text-white" data-id="element-1454">{liveStats.activeGens}</strong>
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0 text-slate-300 text-xs" data-id="element-1455">
              <Layers className="w-3.5 h-3.5 text-blue-400" data-id="element-1456" />
              <span data-id="element-1457">
                Queue:{' '}
                <strong className="text-white" data-id="element-1458">{liveStats.queueLoad}</strong>
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0 text-slate-300 text-xs" data-id="element-1459">
              <Timer className="w-3.5 h-3.5 text-amber-400" data-id="element-1460" />
              <span data-id="element-1461">
                Avg Latency:{' '}
                <strong className="text-white" data-id="element-1462">
                  {Math.round(liveStats.avgLatency)}s
                </strong>
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0 text-slate-300 text-xs" data-id="element-1463">
              <AlertTriangle className={`w-3.5 h-3.5 ${liveStats.failuresLast5m > 0 ? 'text-rose-400' : 'text-slate-500'}`} data-id="element-1464" />
              <span data-id="element-1465">
                Failures (5m):{' '}
                <strong className={liveStats.failuresLast5m > 0 ? 'text-rose-400' : 'text-white'} data-id="element-1466">
                  {liveStats.failuresLast5m}
                </strong>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 w-full lg:w-auto" data-id="element-1467">
            <Button variant="outline" size="sm" className={`text-xs border-slate-700 hover:bg-slate-800 flex-1 lg:flex-none ${enginePaused ? 'text-emerald-400' : 'text-amber-400'}`} icon={enginePaused ? <Play className="w-3.5 h-3.5" data-id="element-1469" /> : <Pause className="w-3.5 h-3.5" data-id="element-1470" />} onClick={() => {
            setEnginePaused(!enginePaused);
            success(enginePaused ? 'Очередь запущена' : 'Очередь приостановлена');
          }} data-id="element-1468">
              {enginePaused ? 'Resume' : 'Pause'}
            </Button>
            <Button variant="outline" size="sm" className="text-xs border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white flex-1 lg:flex-none" icon={<RefreshCw className="w-3.5 h-3.5" data-id="element-1472" />} onClick={() => success('Перезапуск упавших задач...')} data-id="element-1471">
              Retry Failed
            </Button>
          </div>
        </div>

        {/* 2. KPI CARDS - ROW 1 (Volume & Queue) */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-id="element-1473">
          <Card padding="sm" className="rounded-xl shadow-none border-slate-200 p-4 flex flex-col justify-between h-24 cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors group" onClick={() => setActiveTabId('all')} data-id="element-1474">
            <div className="flex justify-between items-start" data-id="element-1475">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors" data-id="element-1476">
                <Hash className="w-4 h-4 text-indigo-600" data-id="element-1477" />
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-400 transition-colors" data-id="element-1478" />
            </div>
            <div data-id="element-1479">
              <div className="text-[11px] text-slate-500 font-medium mb-0.5" data-id="element-1480">
                Всего генераций
              </div>
              <div className="text-xl font-bold text-slate-900" data-id="element-1481">4,208</div>
            </div>
          </Card>

          <Card padding="sm" className="rounded-xl shadow-none border-slate-200 p-4 flex flex-col justify-between h-24 cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-colors group" onClick={() => setActiveTabId('all')} data-id="element-1482">
            <div className="flex justify-between items-start" data-id="element-1483">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors" data-id="element-1484">
                <Zap className="w-4 h-4 text-blue-600" data-id="element-1485" />
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-400 transition-colors" data-id="element-1486" />
            </div>
            <div data-id="element-1487">
              <div className="text-[11px] text-slate-500 font-medium mb-0.5" data-id="element-1488">
                Сегодня
              </div>
              <div className="text-xl font-bold text-slate-900" data-id="element-1489">
                {liveStats.totalToday}
              </div>
            </div>
          </Card>

          <Card padding="sm" className="rounded-xl shadow-none border-slate-200 p-4 flex flex-col justify-between h-24 cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors group" onClick={() => setActiveTabId('queue')} data-id="element-1490">
            <div className="flex justify-between items-start" data-id="element-1491">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors relative" data-id="element-1492">
                <Play className="w-4 h-4 text-emerald-600" data-id="element-1493" />
                <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5" data-id="element-1494">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" data-id="element-1495"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" data-id="element-1496"></span>
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-400 transition-colors" data-id="element-1497" />
            </div>
            <div data-id="element-1498">
              <div className="text-[11px] text-slate-500 font-medium mb-0.5" data-id="element-1499">
                Активные
              </div>
              <div className="text-xl font-bold text-slate-900" data-id="element-1500">
                {liveStats.activeGens}
              </div>
            </div>
          </Card>

          <Card padding="sm" className="rounded-xl shadow-none border-slate-200 p-4 flex flex-col justify-between h-24 cursor-pointer hover:border-amber-300 hover:bg-amber-50/30 transition-colors group" onClick={() => setActiveTabId('queue')} data-id="element-1501">
            <div className="flex justify-between items-start" data-id="element-1502">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors" data-id="element-1503">
                <Clock className="w-4 h-4 text-amber-600" data-id="element-1504" />
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-amber-400 transition-colors" data-id="element-1505" />
            </div>
            <div data-id="element-1506">
              <div className="text-[11px] text-slate-500 font-medium mb-0.5" data-id="element-1507">
                В очереди
              </div>
              <div className="text-xl font-bold text-slate-900" data-id="element-1508">
                {liveStats.queueLoad}
              </div>
            </div>
          </Card>

          <Card padding="sm" className="rounded-xl shadow-none border-rose-200 bg-rose-50/30 p-4 flex flex-col justify-between h-24 cursor-pointer hover:border-rose-300 hover:bg-rose-50 transition-colors group" onClick={() => setActiveTabId('errors')} data-id="element-1509">
            <div className="flex justify-between items-start" data-id="element-1510">
              <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center group-hover:bg-rose-200 transition-colors" data-id="element-1511">
                <AlertTriangle className="w-4 h-4 text-rose-600" data-id="element-1512" />
              </div>
              <ChevronRight className="w-4 h-4 text-rose-300 group-hover:text-rose-500 transition-colors" data-id="element-1513" />
            </div>
            <div data-id="element-1514">
              <div className="text-[11px] text-rose-600 font-medium mb-0.5" data-id="element-1515">
                Ошибки
              </div>
              <div className="text-xl font-bold text-rose-700" data-id="element-1516">18</div>
            </div>
          </Card>

          <Card padding="sm" className="rounded-xl shadow-none border-slate-200 p-4 flex flex-col justify-between h-24 cursor-pointer hover:border-purple-300 hover:bg-purple-50/30 transition-colors group" onClick={() => setActiveTabId('quality')} data-id="element-1517">
            <div className="flex justify-between items-start" data-id="element-1518">
              <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 transition-colors" data-id="element-1519">
                <Eye className="w-4 h-4 text-purple-600" data-id="element-1520" />
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-purple-400 transition-colors" data-id="element-1521" />
            </div>
            <div data-id="element-1522">
              <div className="text-[11px] text-slate-500 font-medium mb-0.5" data-id="element-1523">
                На проверке
              </div>
              <div className="text-xl font-bold text-slate-900" data-id="element-1524">5</div>
            </div>
          </Card>

          <Card padding="sm" className="rounded-xl shadow-none border-emerald-200 bg-emerald-50/30 p-4 flex flex-col justify-between h-24 cursor-pointer hover:border-emerald-300 hover:bg-emerald-50 transition-colors group" onClick={() => setOverviewDrill({
          title: 'Success Rate Analytics',
          type: 'success_rate'
        })} data-id="element-1525">
            <div className="flex justify-between items-start" data-id="element-1526">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors" data-id="element-1527">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-1528" />
              </div>
              <ChevronRight className="w-4 h-4 text-emerald-300 group-hover:text-emerald-500 transition-colors" data-id="element-1529" />
            </div>
            <div data-id="element-1530">
              <div className="text-[11px] text-emerald-700 font-medium mb-0.5" data-id="element-1531">
                Success Rate
              </div>
              <div className="text-xl font-bold text-emerald-700" data-id="element-1532">94.2%</div>
            </div>
          </Card>
        </div>

        {/* 3. KPI CARDS - ROW 2 (Financial & Operational) */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-id="element-1533">
          <Card padding="sm" className="rounded-xl shadow-none border-slate-200 p-3 flex flex-col justify-center cursor-pointer hover:bg-slate-50 transition-colors group" onClick={() => setOverviewDrill({
          title: 'Revenue Breakdown',
          type: 'revenue'
        })} data-id="element-1534">
            <div className="text-[10px] text-slate-500 font-medium mb-1 flex items-center justify-between" data-id="element-1535">
              Revenue AI{' '}
              <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1536" />
            </div>
            <div className="text-sm font-bold text-slate-900" data-id="element-1537">₽847.6K</div>
          </Card>
          <Card padding="sm" className="rounded-xl shadow-none border-emerald-200 bg-emerald-50/30 p-3 flex flex-col justify-center cursor-pointer hover:bg-emerald-50 transition-colors group" onClick={() => setOverviewDrill({
          title: 'Profit Margin Analysis',
          type: 'profit'
        })} data-id="element-1538">
            <div className="text-[10px] text-emerald-700 font-medium mb-1 flex items-center justify-between" data-id="element-1539">
              Profit AI{' '}
              <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1540" />
            </div>
            <div className="text-sm font-bold text-emerald-700" data-id="element-1541">₽601.8K</div>
          </Card>
          <Card padding="sm" className="rounded-xl shadow-none border-slate-200 p-3 flex flex-col justify-center cursor-pointer hover:bg-slate-50 transition-colors group" onClick={() => setOverviewDrill({
          title: 'Margin Analytics',
          type: 'margin'
        })} data-id="element-1542">
            <div className="text-[10px] text-slate-500 font-medium mb-1 flex items-center justify-between" data-id="element-1543">
              Margin{' '}
              <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1544" />
            </div>
            <div className="text-sm font-bold text-slate-900" data-id="element-1545">71%</div>
          </Card>
          <Card padding="sm" className="rounded-xl shadow-none border-slate-200 p-3 flex flex-col justify-center cursor-pointer hover:bg-slate-50 transition-colors group" onClick={() => setOverviewDrill({
          title: 'Cost per Report',
          type: 'cost_per_report'
        })} data-id="element-1546">
            <div className="text-[10px] text-slate-500 font-medium mb-1 flex items-center justify-between" data-id="element-1547">
              Avg Cost/Report{' '}
              <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1548" />
            </div>
            <div className="text-sm font-bold text-slate-900" data-id="element-1549">₽58</div>
          </Card>
          <Card padding="sm" className="rounded-xl shadow-none border-slate-200 p-3 flex flex-col justify-center cursor-pointer hover:bg-slate-50 transition-colors group" onClick={() => setOverviewDrill({
          title: 'Average Selling Price',
          type: 'asp'
        })} data-id="element-1550">
            <div className="text-[10px] text-slate-500 font-medium mb-1 flex items-center justify-between" data-id="element-1551">
              Avg Selling Price{' '}
              <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1552" />
            </div>
            <div className="text-sm font-bold text-slate-900" data-id="element-1553">₽4,200</div>
          </Card>
          <Card padding="sm" className="rounded-xl shadow-none border-rose-200 bg-rose-50/30 p-3 flex flex-col justify-center cursor-pointer hover:bg-rose-50 transition-colors group" onClick={() => setActiveTabId('errors')} data-id="element-1554">
            <div className="text-[10px] text-rose-700 font-medium mb-1 flex items-center justify-between" data-id="element-1555">
              Fail Rate{' '}
              <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1556" />
            </div>
            <div className="text-sm font-bold text-rose-700" data-id="element-1557">2.4%</div>
          </Card>
          <Card padding="sm" className="rounded-xl shadow-none border-slate-200 p-3 flex flex-col justify-center cursor-pointer hover:bg-slate-50 transition-colors group" onClick={() => setOverviewDrill({
          title: 'Retry Analytics',
          type: 'retries'
        })} data-id="element-1558">
            <div className="text-[10px] text-slate-500 font-medium mb-1 flex items-center justify-between" data-id="element-1559">
              Retry Count{' '}
              <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1560" />
            </div>
            <div className="text-sm font-bold text-slate-900" data-id="element-1561">34</div>
          </Card>
          <Card padding="sm" className="rounded-xl shadow-none border-amber-200 bg-amber-50/30 p-3 flex flex-col justify-center cursor-pointer hover:bg-amber-50 transition-colors group" onClick={() => setOverviewDrill({
          title: 'Stuck Jobs',
          type: 'stuck_jobs'
        })} data-id="element-1562">
            <div className="text-[10px] text-amber-700 font-medium mb-1 flex items-center justify-between" data-id="element-1563">
              Stuck Jobs{' '}
              <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1564" />
            </div>
            <div className="text-sm font-bold text-amber-700" data-id="element-1565">2</div>
          </Card>
        </div>

        {/* 4. MAIN CHARTS & ECONOMICS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-1566">
          {/* Generations Chart */}
          <Card padding="md" className="rounded-xl shadow-none border-slate-200 lg:col-span-2 flex flex-col" data-id="element-1567">
            <div className="flex justify-between items-center mb-4" data-id="element-1568">
              <h3 className="text-sm font-bold text-slate-900" data-id="element-1569">
                Генерации по дням
              </h3>
              <div className="flex gap-2" data-id="element-1570">
                <Badge variant="outline" size="sm" className="text-[10px] cursor-pointer hover:bg-slate-50" data-id="element-1571">
                  7 дней
                </Badge>
                <Badge variant="neutral" size="sm" className="text-[10px] cursor-pointer bg-slate-100" data-id="element-1572">
                  14 дней
                </Badge>
                <Badge variant="outline" size="sm" className="text-[10px] cursor-pointer hover:bg-slate-50" data-id="element-1573">
                  30 дней
                </Badge>
              </div>
            </div>
            <div className="flex-1 min-h-[250px] w-full cursor-pointer" onClick={() => setOverviewDrill({
            title: 'Generations Trend',
            type: 'generations_chart'
          })} data-id="element-1574">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1575">
                <AreaChart data={chartData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-1576">
                  <defs data-id="element-1577">
                    <linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1" data-id="element-1578">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-1579" />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-1580" />
                    </linearGradient>
                    <linearGradient id="colorFailed" x1="0" y1="0" x2="0" y2="1" data-id="element-1581">
                      <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} data-id="element-1582" />
                      <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} data-id="element-1583" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-1584" />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#94a3b8'
                }} dy={10} data-id="element-1585" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#94a3b8'
                }} data-id="element-1586" />
                  <RTooltip contentStyle={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                }} labelStyle={{
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#0f172a',
                  marginBottom: '4px'
                }} itemStyle={{
                  fontSize: '12px',
                  padding: '2px 0'
                }} data-id="element-1587" />
                  <Legend iconType="circle" wrapperStyle={{
                  fontSize: '11px',
                  paddingTop: '10px'
                }} data-id="element-1588" />
                  <Area type="monotone" dataKey="success" name="Успешно" stackId="1" stroke="#10b981" fill="url(#colorSuccess)" strokeWidth={2} data-id="element-1589" />
                  <Area type="monotone" dataKey="cancelled" name="Отменено" stackId="1" stroke="#94a3b8" fill="#f1f5f9" strokeWidth={2} data-id="element-1590" />
                  <Area type="monotone" dataKey="failed" name="Ошибка" stackId="1" stroke="#f43f5e" fill="url(#colorFailed)" strokeWidth={2} data-id="element-1591" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Unit Economics */}
          <Card padding="md" className="rounded-xl shadow-none border-slate-200 flex flex-col" data-id="element-1592">
            <div className="flex justify-between items-center mb-4" data-id="element-1593">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2" data-id="element-1594">
                <PieChartIcon className="w-4 h-4 text-indigo-500" data-id="element-1595" /> AI Unit
                Economics
              </h3>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => setOverviewDrill({
              title: 'Full Financial Report',
              type: 'finance'
            })} data-id="element-1596">
                Детали
              </Button>
            </div>

            <div className="space-y-4 flex-1" data-id="element-1597">
              <div className="grid grid-cols-2 gap-3" data-id="element-1598">
                <div className="bg-slate-50 rounded-lg p-3 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setOverviewDrill({
                title: 'Revenue Breakdown',
                type: 'revenue'
              })} data-id="element-1599">
                  <div className="text-[10px] text-slate-500 mb-1" data-id="element-1600">
                    Revenue AI
                  </div>
                  <div className="text-sm font-bold text-slate-900" data-id="element-1601">
                    ₽847,600
                  </div>
                </div>
                <div className="bg-rose-50/50 rounded-lg p-3 cursor-pointer hover:bg-rose-50 transition-colors" onClick={() => setOverviewDrill({
                title: 'API Cost Breakdown',
                type: 'cost'
              })} data-id="element-1602">
                  <div className="text-[10px] text-slate-500 mb-1" data-id="element-1603">
                    API Cost
                  </div>
                  <div className="text-sm font-bold text-rose-600" data-id="element-1604">
                    ₽245,800
                  </div>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100 cursor-pointer hover:bg-emerald-100/50 transition-colors" onClick={() => setOverviewDrill({
              title: 'Profit Margin Analysis',
              type: 'profit'
            })} data-id="element-1605">
                <div className="flex justify-between items-end mb-2" data-id="element-1606">
                  <div data-id="element-1607">
                    <div className="text-[11px] text-emerald-700 font-medium mb-1" data-id="element-1608">
                      Net Profit AI
                    </div>
                    <div className="text-2xl font-bold text-emerald-700" data-id="element-1609">
                      ₽601,800
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1610">
                    <div className="text-[10px] text-emerald-600 mb-1" data-id="element-1611">
                      Margin
                    </div>
                    <div className="text-lg font-bold text-emerald-700" data-id="element-1612">
                      71%
                    </div>
                  </div>
                </div>
                <div className="w-full bg-emerald-200/50 rounded-full h-1.5 mt-2" data-id="element-1613">
                  <div className="bg-emerald-500 h-1.5 rounded-full" style={{
                  width: '71%'
                }} data-id="element-1614"></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2" data-id="element-1615">
                <div className="border border-slate-100 rounded-lg p-3 cursor-pointer hover:border-indigo-200 transition-colors" onClick={() => setOverviewDrill({
                title: 'Cost per Report',
                type: 'cost_per_report'
              })} data-id="element-1616">
                  <div className="text-[10px] text-slate-500 mb-1" data-id="element-1617">
                    Avg Cost / Report
                  </div>
                  <div className="text-sm font-bold text-slate-900" data-id="element-1618">₽58</div>
                </div>
                <div className="border border-slate-100 rounded-lg p-3 cursor-pointer hover:border-indigo-200 transition-colors" onClick={() => setOverviewDrill({
                title: 'Average Selling Price',
                type: 'asp'
              })} data-id="element-1619">
                  <div className="text-[10px] text-slate-500 mb-1" data-id="element-1620">
                    Avg Rev / Report
                  </div>
                  <div className="text-sm font-bold text-slate-900" data-id="element-1621">₽4,200</div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* 5. COST ANALYSIS & QUEUE */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-1622">
          {/* Cost Analysis */}
          <Card padding="md" className="rounded-xl shadow-none border-slate-200 lg:col-span-2" data-id="element-1623">
            <div className="flex justify-between items-center mb-4" data-id="element-1624">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2" data-id="element-1625">
                <DollarSign className="w-4 h-4 text-rose-500" data-id="element-1626" /> Анализ затрат
                (API)
              </h3>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => setOverviewDrill({
              title: 'API Cost Breakdown',
              type: 'cost'
            })} data-id="element-1627">
                Все затраты
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1628">
              <div className="space-y-4" data-id="element-1629">
                <div className="cursor-pointer group" onClick={() => setOverviewDrill({
                title: 'Today Costs',
                type: 'cost_today'
              })} data-id="element-1630">
                  <div className="flex justify-between text-xs mb-1" data-id="element-1631">
                    <span className="text-slate-500 group-hover:text-slate-700" data-id="element-1632">
                      Сегодня
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-1633">$10.44</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-1634">
                    <div className="bg-rose-400 h-1.5 rounded-full" style={{
                    width: '15%'
                  }} data-id="element-1635" />
                  </div>
                </div>
                <div className="cursor-pointer group" onClick={() => setOverviewDrill({
                title: 'Weekly Costs',
                type: 'cost_week'
              })} data-id="element-1636">
                  <div className="flex justify-between text-xs mb-1" data-id="element-1637">
                    <span className="text-slate-500 group-hover:text-slate-700" data-id="element-1638">
                      За неделю
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-1639">$65.04</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-1640">
                    <div className="bg-rose-400 h-1.5 rounded-full" style={{
                    width: '45%'
                  }} data-id="element-1641" />
                  </div>
                </div>
                <div className="cursor-pointer group" onClick={() => setOverviewDrill({
                title: 'Monthly Costs',
                type: 'cost_month'
              })} data-id="element-1642">
                  <div className="flex justify-between text-xs mb-1" data-id="element-1643">
                    <span className="text-slate-500 group-hover:text-slate-700" data-id="element-1644">
                      За месяц
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-1645">$245.80</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-1646">
                    <div className="bg-rose-500 h-1.5 rounded-full" style={{
                    width: '85%'
                  }} data-id="element-1647" />
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-100" data-id="element-1648">
                  <div className="flex justify-between items-center cursor-pointer group" onClick={() => setOverviewDrill({
                  title: 'Cost Forecast',
                  type: 'forecast'
                })} data-id="element-1649">
                    <span className="text-xs text-slate-500 group-hover:text-slate-700" data-id="element-1650">
                      Прогноз на месяц
                    </span>
                    <span className="text-sm font-bold text-rose-600" data-id="element-1651">
                      ~$310.00
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4" data-id="element-1652">
                <div data-id="element-1653">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-1654">
                    Cost by Model
                  </div>
                  <div className="space-y-2" data-id="element-1655">
                    <div className="flex justify-between items-center text-xs cursor-pointer hover:bg-slate-50 p-1 rounded" onClick={() => setOverviewDrill({
                    title: 'GPT-5 Costs',
                    type: 'cost_model'
                  })} data-id="element-1656">
                      <span className="text-slate-600" data-id="element-1657">GPT-5</span>
                      <span className="font-medium" data-id="element-1658">$180.50</span>
                    </div>
                    <div className="flex justify-between items-center text-xs cursor-pointer hover:bg-slate-50 p-1 rounded" onClick={() => setOverviewDrill({
                    title: 'Claude 3.5 Costs',
                    type: 'cost_model'
                  })} data-id="element-1659">
                      <span className="text-slate-600" data-id="element-1660">Claude 3.5</span>
                      <span className="font-medium" data-id="element-1661">$45.20</span>
                    </div>
                    <div className="flex justify-between items-center text-xs cursor-pointer hover:bg-slate-50 p-1 rounded" onClick={() => setOverviewDrill({
                    title: 'Llama 3 Costs',
                    type: 'cost_model'
                  })} data-id="element-1662">
                      <span className="text-slate-600" data-id="element-1663">Llama 3</span>
                      <span className="font-medium" data-id="element-1664">$20.10</span>
                    </div>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5 flex items-start gap-2 cursor-pointer hover:bg-amber-100/50 transition-colors" onClick={() => setOverviewDrill({
                title: 'Budget Alerts',
                type: 'budget'
              })} data-id="element-1665">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" data-id="element-1666" />
                  <div data-id="element-1667">
                    <div className="text-xs font-bold text-amber-800" data-id="element-1668">
                      Budget Alert
                    </div>
                    <div className="text-[10px] text-amber-700 mt-0.5" data-id="element-1669">
                      Projected cost ($310) exceeds monthly budget ($300) by 3%.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Queue Mini-Overview */}
          <Card padding="md" className="rounded-xl shadow-none border-slate-200 flex flex-col" data-id="element-1670">
            <div className="flex justify-between items-center mb-4" data-id="element-1671">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2" data-id="element-1672">
                <Layers className="w-4 h-4 text-blue-500" data-id="element-1673" /> Очередь
              </h3>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => setActiveTabId('queue')} data-id="element-1674">
                В очередь
              </Button>
            </div>

            <div className="space-y-3 flex-1" data-id="element-1675">
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setActiveTabId('queue')} data-id="element-1676">
                <div className="flex items-center gap-2" data-id="element-1677">
                  <div className="w-2 h-2 rounded-full bg-rose-500" data-id="element-1678" />
                  <span className="text-xs font-medium text-slate-700" data-id="element-1679">
                    High Priority
                  </span>
                </div>
                <span className="text-xs font-bold text-slate-900" data-id="element-1680">2</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setActiveTabId('queue')} data-id="element-1681">
                <div className="flex items-center gap-2" data-id="element-1682">
                  <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-1683" />
                  <span className="text-xs font-medium text-slate-700" data-id="element-1684">
                    Normal Priority
                  </span>
                </div>
                <span className="text-xs font-bold text-slate-900" data-id="element-1685">8</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setActiveTabId('queue')} data-id="element-1686">
                <div className="flex items-center gap-2" data-id="element-1687">
                  <div className="w-2 h-2 rounded-full bg-slate-400" data-id="element-1688" />
                  <span className="text-xs font-medium text-slate-700" data-id="element-1689">
                    Low Priority
                  </span>
                </div>
                <span className="text-xs font-bold text-slate-900" data-id="element-1690">2</span>
              </div>

              <div className="flex items-center justify-between p-2 bg-amber-50 border border-amber-100 rounded-lg cursor-pointer hover:bg-amber-100/50 transition-colors mt-4" onClick={() => setOverviewDrill({
              title: 'Stuck Jobs',
              type: 'stuck_jobs'
            })} data-id="element-1691">
                <div className="flex items-center gap-2" data-id="element-1692">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600" data-id="element-1693" />
                  <span className="text-xs font-medium text-amber-800" data-id="element-1694">
                    Stuck &gt; 5 min
                  </span>
                </div>
                <span className="text-xs font-bold text-amber-700" data-id="element-1695">2</span>
              </div>
            </div>
          </Card>
        </div>

        {/* 6. TEMPLATES & ERRORS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-1696">
          {/* Top Templates */}
          <Card padding="md" className="rounded-xl shadow-none border-slate-200" data-id="element-1697">
            <div className="flex justify-between items-center mb-4" data-id="element-1698">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2" data-id="element-1699">
                <FileCode2 className="w-4 h-4 text-indigo-500" data-id="element-1700" /> Шаблоны
                отчётов
              </h3>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => setActiveTabId('templates')} data-id="element-1701">
                Все шаблоны
              </Button>
            </div>
            <div className="overflow-x-auto" data-id="element-1702">
              <table className="w-full text-left text-xs" data-id="element-1703">
                <thead className="text-slate-400 border-b border-slate-100" data-id="element-1704">
                  <tr data-id="element-1705">
                    <th className="pb-2 font-medium" data-id="element-1706">Шаблон</th>
                    <th className="pb-2 font-medium text-right" data-id="element-1707">Ген.</th>
                    <th className="pb-2 font-medium text-right" data-id="element-1708">Success</th>
                    <th className="pb-2 font-medium text-right" data-id="element-1709">Avg Time</th>
                    <th className="pb-2 font-medium text-right" data-id="element-1710">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50" data-id="element-1711">
                  <tr className="cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setOverviewDrill({
                  title: 'Template: Натальная карта',
                  type: 'template_stats'
                })} data-id="element-1712">
                    <td className="py-2.5 font-medium text-slate-800" data-id="element-1713">
                      Натальная карта
                    </td>
                    <td className="py-2.5 text-right text-slate-600" data-id="element-1714">1,842</td>
                    <td className="py-2.5 text-right text-emerald-600" data-id="element-1715">
                      98.2%
                    </td>
                    <td className="py-2.5 text-right text-slate-600" data-id="element-1716">42s</td>
                    <td className="py-2.5 text-right font-medium text-slate-900" data-id="element-1717">
                      ₽420K
                    </td>
                  </tr>
                  <tr className="cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setOverviewDrill({
                  title: 'Template: Совместимость',
                  type: 'template_stats'
                })} data-id="element-1718">
                    <td className="py-2.5 font-medium text-slate-800" data-id="element-1719">
                      Совместимость
                    </td>
                    <td className="py-2.5 text-right text-slate-600" data-id="element-1720">1,205</td>
                    <td className="py-2.5 text-right text-emerald-600" data-id="element-1721">
                      95.4%
                    </td>
                    <td className="py-2.5 text-right text-slate-600" data-id="element-1722">58s</td>
                    <td className="py-2.5 text-right font-medium text-slate-900" data-id="element-1723">
                      ₽280K
                    </td>
                  </tr>
                  <tr className="cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setOverviewDrill({
                  title: 'Template: Соляр',
                  type: 'template_stats'
                })} data-id="element-1724">
                    <td className="py-2.5 font-medium text-slate-800" data-id="element-1725">Соляр</td>
                    <td className="py-2.5 text-right text-slate-600" data-id="element-1726">840</td>
                    <td className="py-2.5 text-right text-amber-600" data-id="element-1727">88.5%</td>
                    <td className="py-2.5 text-right text-slate-600" data-id="element-1728">65s</td>
                    <td className="py-2.5 text-right font-medium text-slate-900" data-id="element-1729">
                      ₽110K
                    </td>
                  </tr>
                  <tr className="cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setOverviewDrill({
                  title: 'Template: Комбо',
                  type: 'template_stats'
                })} data-id="element-1730">
                    <td className="py-2.5 font-medium text-slate-800" data-id="element-1731">Комбо</td>
                    <td className="py-2.5 text-right text-slate-600" data-id="element-1732">321</td>
                    <td className="py-2.5 text-right text-rose-600" data-id="element-1733">82.1%</td>
                    <td className="py-2.5 text-right text-rose-600" data-id="element-1734">112s</td>
                    <td className="py-2.5 text-right font-medium text-slate-900" data-id="element-1735">
                      ₽37K
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>

          {/* Top Errors */}
          <Card padding="md" className="rounded-xl shadow-none border-slate-200" data-id="element-1736">
            <div className="flex justify-between items-center mb-4" data-id="element-1737">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2" data-id="element-1738">
                <AlertCircle className="w-4 h-4 text-rose-500" data-id="element-1739" /> Типы ошибок
              </h3>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => setActiveTabId('errors')} data-id="element-1740">
                Все ошибки
              </Button>
            </div>
            <div className="space-y-3" data-id="element-1741">
              <div className="flex items-center justify-between p-2.5 border border-slate-100 rounded-lg cursor-pointer hover:border-rose-200 hover:bg-rose-50/30 transition-colors" onClick={() => setActiveTabId('errors')} data-id="element-1742">
                <div data-id="element-1743">
                  <div className="text-xs font-bold text-slate-800 mb-0.5" data-id="element-1744">
                    API Timeout (120s)
                  </div>
                  <div className="text-[10px] text-slate-500" data-id="element-1745">
                    Чаще всего: Комбо, Соляр
                  </div>
                </div>
                <div className="text-right" data-id="element-1746">
                  <div className="text-sm font-bold text-rose-600" data-id="element-1747">42</div>
                  <div className="text-[10px] text-slate-400" data-id="element-1748">за неделю</div>
                </div>
              </div>
              <div className="flex items-center justify-between p-2.5 border border-slate-100 rounded-lg cursor-pointer hover:border-rose-200 hover:bg-rose-50/30 transition-colors" onClick={() => setActiveTabId('errors')} data-id="element-1749">
                <div data-id="element-1750">
                  <div className="text-xs font-bold text-slate-800 mb-0.5" data-id="element-1751">
                    Token overflow (max 8192)
                  </div>
                  <div className="text-[10px] text-slate-500" data-id="element-1752">
                    Чаще всего: Claude 3.5
                  </div>
                </div>
                <div className="text-right" data-id="element-1753">
                  <div className="text-sm font-bold text-rose-600" data-id="element-1754">18</div>
                  <div className="text-[10px] text-slate-400" data-id="element-1755">за неделю</div>
                </div>
              </div>
              <div className="flex items-center justify-between p-2.5 border border-slate-100 rounded-lg cursor-pointer hover:border-rose-200 hover:bg-rose-50/30 transition-colors" onClick={() => setActiveTabId('errors')} data-id="element-1756">
                <div data-id="element-1757">
                  <div className="text-xs font-bold text-slate-800 mb-0.5" data-id="element-1758">
                    Rate limit exceeded
                  </div>
                  <div className="text-[10px] text-slate-500" data-id="element-1759">
                    Провайдер: OpenAI
                  </div>
                </div>
                <div className="text-right" data-id="element-1760">
                  <div className="text-sm font-bold text-rose-600" data-id="element-1761">12</div>
                  <div className="text-[10px] text-slate-400" data-id="element-1762">за неделю</div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>;
  };
  const renderQueue = () => {
    return <AIQueueManager success={success} setDetailPanel={setDetailPanel} data-id="element-1763" />;
  };
  const renderAll = () => {
    return <AIAllGenerationsManager success={success} setDetailPanel={setDetailPanel} data-id="element-1764" />;
  };
  const renderErrors = () => <AIErrorControlCenter success={success} setDetailPanel={setDetailPanel} data-id="element-1765" />;
  const renderTemplates = () => <AITemplateEngine success={success} setDetailPanel={setDetailPanel} data-id="element-1766" />;
  const renderSettings = () => <AIEngineControlCenter success={success} data-id="element-1767" />;
  const renderQuality = () => <AIQualityControlCenter success={success} setDetailPanel={setDetailPanel} data-id="element-1768" />;
  return <div className="space-y-6 relative" data-id="element-1769">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1770">
        <div data-id="element-1771">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-1772">AI / ИИ-отчёты</h2>
          <p className="text-sm text-slate-500" data-id="element-1773">
            Управление генерацией, шаблонами и качеством AI-отчётов
          </p>
        </div>
        <Button variant="primary" icon={<Play className="w-4 h-4" data-id="element-1775" />} onClick={() => setShowSandbox(true)} data-id="element-1774">
          Тест AI (Песочница)
        </Button>
      </div>

      <Tabs activeTab={activeTabId} onChange={setActiveTabId} tabs={[{
      id: 'overview',
      label: 'Обзор',
      content: renderOverview()
    }, {
      id: 'queue',
      label: 'Очередь',
      content: renderQueue()
    }, {
      id: 'all',
      label: 'Все генерации',
      content: renderAll()
    }, {
      id: 'errors',
      label: 'Ошибки',
      content: renderErrors()
    }, {
      id: 'templates',
      label: 'Шаблоны',
      content: renderTemplates()
    }, {
      id: 'settings',
      label: 'Настройки',
      content: renderSettings()
    }, {
      id: 'quality',
      label: 'Качество',
      content: renderQuality()
    }]} data-id="element-1776" />

      {/* Overview Drill-down Modal */}
      {overviewDrill && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={e => {
      if (e.target === e.currentTarget) setOverviewDrill(null);
    }} data-id="element-1777">
          <Card className="w-full max-w-2xl shadow-2xl overflow-hidden" padding="none" data-id="element-1778">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50" data-id="element-1779">
              <div className="flex items-center gap-2" data-id="element-1780">
                <BarChart3 className="w-5 h-5 text-indigo-600" data-id="element-1781" />
                <h3 className="font-bold text-slate-900 text-sm" data-id="element-1782">
                  {overviewDrill.title}
                </h3>
              </div>
              <button onClick={() => setOverviewDrill(null)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100" data-id="element-1783">
                <X className="w-5 h-5" data-id="element-1784" />
              </button>
            </div>
            <div className="p-6" data-id="element-1785">
              <div className="flex flex-col items-center justify-center py-12 text-slate-500" data-id="element-1786">
                <Database className="w-12 h-12 text-slate-300 mb-4" data-id="element-1787" />
                <p className="text-sm font-medium" data-id="element-1788">
                  Детализация для: {overviewDrill.title}
                </p>
                <p className="text-xs text-slate-400 mt-2" data-id="element-1789">
                  В реальной системе здесь будет таблица с данными (type:{' '}
                  {overviewDrill.type})
                </p>
                <Button variant="outline" size="sm" className="mt-6" onClick={() => setOverviewDrill(null)} data-id="element-1790">
                  Закрыть
                </Button>
              </div>
            </div>
          </Card>
        </div>}

      {/* AI Sandbox Modal */}
      {showSandbox && <AISandboxModal open={showSandbox} onClose={() => setShowSandbox(false)} success={success} data-id="element-1791" />}
    </div>;
}